import { useEffect, useRef, useState } from "react";
import Menu from "./components/Menu.jsx";
import Splash from "./components/Splash.jsx";
import Lock from "./screens/Lock.jsx";
import Home from "./screens/Home.jsx";
import Tribe from "./screens/Tribe.jsx";
import Thread from "./screens/Thread.jsx";
import Wall from "./screens/Wall.jsx";
import Ops from "./screens/Ops.jsx";
import Culture from "./screens/Culture.jsx";
import Launch from "./screens/Launch.jsx";
import DreamTeam from "./screens/DreamTeam.jsx";
import LeadershipPlus from "./screens/LeadershipPlus.jsx";
import Onboard from "./screens/Onboard.jsx";
import Report from "./screens/Report.jsx";
import Search from "./screens/Search.jsx";
import Dashboard from "./screens/Dashboard.jsx";
import Org from "./screens/Org.jsx";
import Meet from "./screens/Meet.jsx";
import Calendar from "./screens/Calendar.jsx";
import Worship from "./screens/Worship.jsx";
import Admin from "./screens/Admin.jsx";
import Creative from "./screens/Creative.jsx";
import Docs from "./screens/Docs.jsx";
import Incidents from "./screens/Incidents.jsx";
import IncidentModal from "./components/IncidentModal.jsx";
import ShipHappens from "./screens/ShipHappens.jsx";
import Analytics from "./screens/Analytics.jsx";
import { SPLASH_LINES } from "./data/site.js";
import { rankOf } from "./data/levels.js";
import { trackView } from "./lib/track.js";
import { setBadge } from "./lib/badge.js";

// Every view is addressable, so a page can be linked to and bookmarked and the
// browser's own back button works. "home" is "/", everything else is "/<view>".
const VIEWS = [
  "home", "tribe", "thread", "wall", "ops", "creative", "culture", "launch",
  "docs", "ship-happens", "dreamteam", "leadershipplus", "onboard", "report", "search",
  "dashboard", "org", "meet", "calendar", "worship", "analytics", "admin",
  "incidents",
];

// Mirrors the tier gates in Menu.jsx. The menu hides a row below its tier, but
// a URL can be typed — so the guard has to live here too, or making views
// addressable would quietly hand out everything the menu was hiding.
const NEEDS = {
  leadershipplus: "leader",
  meet: "leader",
  org: "director",
  analytics: "senior",
  // The screen is leader-reachable, but it only ever shows what the API
  // returns — senior sees everything, a leader sees reports naming them.
  incidents: "leader",
  admin: "senior",
};

const pathFor = (view) => (view === "home" ? "/" : "/" + view);

const viewFromPath = (pathname) => {
  const slug = String(pathname || "").replace(/^\/+|\/+$/g, "");
  if (!slug) return "home";
  return VIEWS.includes(slug) ? slug : "home";
};

const SPLASH_FIRST_MS = 1200;
const SPLASH_NAV_MS = 750;

export default function App() {
  const [me, setMe] = useState(null);
  const [, setChecked] = useState(false);
  const [view, setView] = useState(() => viewFromPath(window.location.pathname));
  const [chatNode, setChatNode] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [splash, setSplash] = useState(true);
  const [splashLine, setSplashLine] = useState(SPLASH_LINES[0]);
  const [unread, setUnread] = useState(0);
  const [incidentOpen, setIncidentOpen] = useState(false);

  // Per-view scroll offsets, so going back lands where you left.
  const scrollPositions = useRef({});

  useEffect(() => {
    const t = setTimeout(() => setSplash(false), SPLASH_FIRST_MS);
    return () => clearTimeout(t);
  }, []);

  // Signing in is sticky, and staying signed in has to survive a bad network.
  //
  // This used to treat any failed /api/me as signed out, so a cold PWA start on
  // a weak connection dropped straight to the lock screen even though the
  // cookie was perfectly good — indistinguishable, from the outside, from being
  // logged out. Only a 401 means signed out now. Anything else falls back to
  // the last known identity so the app still opens, and the server re-checks
  // every gated call regardless, so a stale cache cannot grant anything.
  useEffect(() => {
    const CACHE = "hub:me";

    const remember = (data) => {
      try {
        localStorage.setItem(CACHE, JSON.stringify(data));
      } catch {
        /* private mode or quota; the session cookie is still the real state */
      }
    };

    const cached = () => {
      try {
        const raw = localStorage.getItem(CACHE);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    };

    const enter = (data) => {
      // The storage helper reads this to stamp posts with the author's pid.
      window.__me = data;
      setMe(data);
    };

    fetch("/api/me")
      .then(async (r) => {
        if (r.ok) {
          const data = await r.json();
          if (data && data.level) {
            remember(data);
            enter(data);
          }
          return;
        }
        // A real signed-out answer. Drop the cache so the next open agrees.
        if (r.status === 401) {
          try {
            localStorage.removeItem(CACHE);
          } catch {
            /* nothing to clear */
          }
          return;
        }
        throw new Error("unreachable");
      })
      .catch(() => {
        const last = cached();
        if (last && last.level) enter(last);
      })
      .finally(() => setChecked(true));
  }, []);

  useEffect(() => {
    if (!splash) window.scrollTo(0, scrollPositions.current[view] || 0);
  }, [splash, view]);

  // Back and forward. The handler reads the URL rather than closing over
  // `view`, so it stays correct however many times the user navigates.
  useEffect(() => {
    const onPop = () => {
      const next = viewFromPath(window.location.pathname);
      setSplashLine(SPLASH_LINES[Math.floor(Math.random() * SPLASH_LINES.length)]);
      setSplash(true);
      setView(next);
      setTimeout(() => setSplash(false), SPLASH_NAV_MS);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // safeView keeps gated content from painting; this puts the address bar back
  // in step, so a member who typed /admin does not sit on that URL.
  useEffect(() => {
    if (!me) return;
    const need = NEEDS[view];
    if (need && rankOf(me.level) < rankOf(need)) {
      setView("home");
      window.history.replaceState({ view: "home" }, "", "/");
    }
  }, [me, view]);

  // One beacon per view once signed in. Guests are skipped server-side.
  useEffect(() => {
    if (me) trackView(view);
  }, [me, view]);

  // The heart on the pass. Guests have nothing to read, so this is skipped for
  // them rather than returning a pointless zero.
  useEffect(() => {
    if (!me || String(me.pid || "").startsWith("guest-")) return;
    fetch("/api/notifications")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const total = d?.total || 0;
        setUnread(total);
        // The installed icon carries the same number the pass does.
        setBadge(total);
      })
      .catch(() => {});
  }, [me]);

  // Opening the dashboard is what "reading" means — the three app streams all
  // live there. Planning Center requests stay counted until answered in PCO.
  const markRead = () => {
    fetch("/api/notifications", { method: "POST", body: "{}" })
      .then(() => fetch("/api/notifications"))
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const total = d?.total || 0;
        setUnread(total);
        setBadge(total);
      })
      .catch(() => {});
  };

  // `push` is false when the browser already moved us, so back and forward do
  // not stack duplicate entries on top of the one the user just left.
  const go = (next, { push = true } = {}) => {
    if (next === "dashboard") markRead();
    scrollPositions.current[view] = window.scrollY;
    if (!(next in scrollPositions.current)) scrollPositions.current[next] = 0;
    setSplashLine(SPLASH_LINES[Math.floor(Math.random() * SPLASH_LINES.length)]);
    setSplash(true);
    setView(next);
    if (push && window.location.pathname !== pathFor(next)) {
      window.history.pushState({ view: next }, "", pathFor(next));
    }
    setTimeout(() => setSplash(false), SPLASH_NAV_MS);
  };

  const signOut = () => {
    // Clear the offline identity too, or the reload would read it back and the
    // lock screen would never appear.
    try {
      localStorage.removeItem("hub:me");
    } catch {
      /* nothing to clear */
    }
    fetch("/api/logout", { method: "POST" }).finally(() => window.location.reload());
  };

  const enterAsGuest = async (payload) => {
    try {
      const r = await fetch("/api/guest", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        const data = await r.json();
        window.__me = data;
        setMe(data);
        return true;
      }
    } catch {
      /* fall through to the error message */
    }
    return false;
  };

  if (splash) {
    return (
      <div className="hub">
        <Splash line={splashLine} />
      </div>
    );
  }

  if (!me) {
    return (
      <div className="hub">
        <Lock onGuest={enterAsGuest} onSignedIn={() => window.location.reload()} />
      </div>
    );
  }

  const userRank = rankOf(me.level);
  const unlinked = me.linked === false;
  const firstName = me.first || null;
  const userName = firstName || "Anonymous";
  const home = () => go("home");

  // A URL above the visitor's tier resolves to home rather than rendering.
  // Decided during render so gated content never paints, even for a frame.
  const needed = NEEDS[view];
  const safeView = needed && userRank < rankOf(needed) ? "home" : view;

  // Sub-pages all render the same shell. The backdrop is the fixed skyline
  // photograph on .hub itself, so there is no backdrop element here.
  const shell = (node) => (
    <div className="hub">
      {node}
    </div>
  );

  switch (safeView) {
    case "tribe":
      return shell(
        <Tribe
          back={home}
          onChat={(node) => {
            setChatNode(node);
            go("thread");
          }}
        />,
      );
    case "thread":
      return chatNode
        ? shell(<Thread back={() => go("tribe")} node={chatNode} userName={userName} />)
        : shell(<Tribe back={home} onChat={(n) => { setChatNode(n); go("thread"); }} />);
    case "wall":
      return shell(<Wall back={home} userName={userName} userRank={userRank} />);
    case "ops":
      return shell(<Ops back={home} go={go} userRank={userRank} team={me.team || null} />);
    case "incidents":
      return shell(<Incidents back={home} />);
    case "creative":
      return shell(<Creative back={home} />);
    case "docs":
      // The screen is member-reachable; /api/docs decides which documents it
      // is allowed to return, so there is no tier gate on the route itself.
      return shell(<Docs back={home} level={me.level} />);
    case "culture":
      return shell(<Culture back={home} />);
    case "launch":
      return shell(<Launch back={home} />);
    case "ship-happens":
      return shell(<ShipHappens back={home} />);
    case "dreamteam":
      return shell(<DreamTeam back={home} />);
    case "leadershipplus":
      return shell(<LeadershipPlus back={home} userName={userName} />);
    case "onboard":
      return shell(<Onboard back={home} go={go} />);
    case "report":
      return shell(<Report back={home} userName={userName} />);
    case "search":
      return shell(<Search back={home} go={go} userRank={userRank} />);
    case "dashboard":
      return shell(
        <Dashboard
          back={home}
          go={go}
          userName={userName}
          userRank={userRank}
          level={me.level}
          pid={me.pid}
        />,
      );
    case "org":
      return shell(<Org back={home} />);
    case "meet":
      return shell(<Meet back={home} userName={userName} userRank={userRank} />);
    case "calendar":
      return shell(<Calendar back={home} userRank={userRank} />);
    case "worship":
      return shell(<Worship back={home} />);
    case "analytics":
      // Senior-only in the menu; /api/analytics re-checks the tier regardless.
      return shell(<Analytics back={home} />);
    case "admin":
      // The screen is only reachable from the senior-only menu entry, and the
      // API behind it re-checks the tier server-side regardless.
      return shell(<Admin back={home} />);
    default:
      return (
        <div className="hub">
          <button className="menu-btn" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
            ☰
          </button>
          {!unlinked && (
            <button
              className="menu-btn search-btn"
              aria-label="Search"
              onClick={() => go("search")}
            >
              🔍
            </button>
          )}
          <Menu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            onNav={go}
            onIncident={() => setIncidentOpen(true)}
            userRank={userRank}
            onLock={signOut}
            unlinked={unlinked}
            canFile={me.canFile === true}
          />
          {incidentOpen && (
            <IncidentModal
              reporter={me.name}
              // Whether they will be able to open the report again, so the
              // confirmation tells them the truth rather than the leader's
              // version of it.
              canRead={userRank >= rankOf("leader")}
              onClose={() => setIncidentOpen(false)}
            />
          )}
          <Home
            go={go}
            onSignOut={signOut}
            me={me}
            userRank={userRank}
            firstName={firstName}
            unread={unread}
          />
        </div>
      );
  }
}
